import type { SchemaInput } from './schema-input.js';
import { isString } from './runtime-values.js';
export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de', 'pt', 'zh', 'ja', 'ru', 'ar'] as const;
export const RTL_LOCALES: ReadonlySet<SupportedLocale> = new Set(['ar']);

export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export function isSupportedLocale(value: SchemaInput): value is SupportedLocale {
  // SAFETY: The adjacent array check validates the collection and its elements before use.
  return isString(value) && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function localeDirection(locale: string): 'ltr' | 'rtl' {
  // SAFETY: The adjacent allowlist, normalizer, or equality check limits this value to the stated union.
  return RTL_LOCALES.has(locale as SupportedLocale) ? 'rtl' : 'ltr';
}

export function normalizeLocale(value: SchemaInput): SupportedLocale {
  const language = isString(value) ? value.trim().toLowerCase().split('-')[0] : '';
  return isSupportedLocale(language) ? language : 'en';
}

export function localeFromRequest(request: Request, explicitLocale?: SchemaInput): SupportedLocale {
  if (isSupportedLocale(explicitLocale)) return explicitLocale;
  const referer = request.headers.get('referer');
  if (referer) {
    try {
      const [, locale] = new URL(referer).pathname.split('/');
      return normalizeLocale(locale);
    } catch { /* use Accept-Language below */ }
  }
  return normalizeLocale(request.headers.get('accept-language')?.split(',')[0]);
}

export function localePath(path: string, locale: SupportedLocale): string {
  if (locale === 'en') return path;
  return `/${locale}${path.startsWith('/') ? path : `/${path}`}`;
}
